import React from 'react';
import {observer} from 'mobx-react';
import {InteractiveAccordionTable, DropdownFilter, ActionLink} from 'components';
import {FormattedMessage} from 'react-intl';
import AddAssigneesModal from './AddAssigneesModal';
import ArchiveCompanyDocumentModal from './ArchiveCompanyDocumentModal';
import UnarchiveCompanyDocumentModal from './UnarchiveCompanyDocumentModal';
import CompanyDocument from 'main/stores/documents/CompanyDocument';
import Folder from 'main/stores/folders/Folder';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';

const PreviewLink = observer(({model}) => {
  return (
    <a className='TextLink dodger'
       href={`/api/v1/company_documents/${model.id}/preview`}
       target='_blank'>
      <FormattedMessage id='company_settings.documents.Preview' />
    </a>
  );
});

export const CompanyDocumentNameCell = observer(({model, level, uiState}) => {
  const companyDocument = model;
  return (
    <div>
      {!!companyDocument.folderName && level === 0 && <div className='flex align-items-middle mb1'>
        <i className='jumbo mr1 material-icons h4'>arrow_forward</i>
        <span className='jumbo'>{companyDocument.folderName}</span>
      </div>}
      {companyDocument.canPatch ?
        <ActionLink onClick={() => uiState.goToEditDocument(companyDocument)}>{companyDocument.name}</ActionLink>
        :
        <span>{companyDocument.name}</span>
      }
    </div>
  );
});

const COLUMNS = [
  {
    header: 'company_settings.documents.Name',
    component: CompanyDocumentNameCell,
    width: 6
  },
  {
    header: 'company_settings.documents.Document Type',
    attribute: 'typeView',
    width: 3
  },
  {
    component: PreviewLink,
    width: 2
  }
];

const FOLDER_COLUMNS = [
  {
    header: 'company_settings.documents.Name',
    attribute: 'name',
    width: 11
  },
];

const LOCALIZATION = {
  removeModal: {
    header: 'company_settings.documents.Remove Company Document',
    subHeader: 'company_settings.documents.Are you sure you want to remove this company document?',
    body: 'company_settings.documents.Documents already completed by employees will not be removed. Documents assigned to employees but not yet completed will be removed.'
  },
  interactive: {
    addModel: 'company_settings.documents.+ Add a Document',
  },
  emptyStateMessage: 'company_settings.documents.No documents to display'
};

const NESTED_ROWS = [
  {
    attribute: 'companyDocuments'
  }
];

const CompanyDocumentsTable = observer(({uiState}) => {
  return (
    <div className='mb2'>
      <InteractiveAccordionTable
        title={t('company_settings.documents.Company Documents')}
        saveStateToUrl
        uiState={uiState}
        columns={COLUMNS}
          localization={LOCALIZATION}
          folderProxy={{
            endpoint: endpoints.FOLDERS.withFilter,
            model: Folder,
            modelType: types.FOLDER
          }}
          folderColumns={FOLDER_COLUMNS}
          canExpand={item => item.hasDocuments}
          onExpand={(folder, filter) => uiState.fetchFolderDocuments(folder, filter)}
          nestedRows={NESTED_ROWS}
          editRoute='/documents/edit/:id'
          onAdd={() => uiState.openAddDocumentModal()}
          onRemove={model => uiState.removeCompanyDocument(model)}
          customLinks={model => uiState.customLinksFor(model)}
          onMount={agent => uiState.setInteractiveAgent(agent)}
          proxy={{
            type: 'async',
            modelType: types.COMPANY_DOCUMENT,
            model: CompanyDocument,
            endpoint: endpoints.COMPANY_DOCUMENTS.withFolder()
          }}
          initialFilter={{
            status: uiState.filterOptions[0].id
          }}
        >
          <DropdownFilter options={uiState.filterOptions} attr='status'/>
      </InteractiveAccordionTable>
      <ArchiveCompanyDocumentModal
        isOpen={uiState.archiveDocumentModalOpen}
        onCancel={() => uiState.closeArchiveDocumentModal()}
        onArchive={() => uiState.archiveCompanyDocument()}
      />
      <UnarchiveCompanyDocumentModal
        isOpen={uiState.unarchiveDocumentModalOpen}
        onCancel={() => uiState.closeUnarchiveDocumentModal()}
        onUnarchive={() => uiState.unarchiveCompanyDocument()}
      />
      <AddAssigneesModal
        companyDocument={uiState.editingDocument}
        isOpen={uiState.addAssigneeModalOpen}
        onHide={() => uiState.closeAddAssigneeModal()}
        onSuccess={() => uiState.onAssigneesAdded()} />
    </div>
  );
});

export default CompanyDocumentsTable;
